// client/src/components/common/PickerTimeChooser.tsx
//
// Time of day in one or two taps (ST-00264; replaces ST-00204's − / + steppers, which took many
// taps to get from 06:00 to 14:35): every hour 00–23 as a 44 px target (6 × 4), then the minute
// on the 5-minute grid (6 × 2). Any other minute is typed in "Type a time" — 1435, 14:35, 14h35,
// 2:35 pm — read as you type; an unreadable entry says so and the sheet keeps Apply off until
// it is fixed or cleared. Choosing an hour moves on to the minutes.
//
// Each grid is a radiogroup with one tab stop: the arrow keys move within it (Home / End to its
// ends), Enter or Space chooses. Nothing wraps round midnight and the date never changes here.
// The time chosen is printed large and announced (unless the sheet's own hint already says it).
//
// In the desktop popup (ST-00294) the same chooser is compact (30 px cells, PickerPopover.css)
// and "Type a time" comes first; a time-only popup focuses it on open (`typedRef`) and Enter
// there applies the popup (`onEnter`) once what was typed reads as a time — 1435, Enter — and
// lays the grids out wide and short (`wide`: twelve across, up / down move by twelve).

import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type Ref,
} from 'react';
import { revealTime, useLatest } from './pickerHooks';
import { usePickerVariant } from './PickerSurface';
import {
  formatHm,
  minuteGrid,
  nearestGridMinute,
  pad2,
  parseTypedTime,
  sameTime,
  type HourMinute,
} from './pickerLogic';

const HOURS: readonly number[] = Array.from({ length: 24 }, (_, h) => h);
/** Six across (the hours read as night / morning / afternoon / evening rows); twelve when wide. */
const COLUMNS = 6;
const WIDE_COLUMNS = 12;

interface ChoiceGridProps {
  unit: 'hour' | 'minute';
  labelId: string;
  options: readonly number[];
  selected: number | null;
  /** Holds the tab stop (dashed) when nothing is selected: the nearest grid minute. */
  near: number | null;
  onChoose: (n: number) => void;
  /** Receives the option that holds the tab stop. */
  tabStopRef?: Ref<HTMLButtonElement>;
  gridRef?: Ref<HTMLDivElement>;
  /** Options per row: the arrow keys up / down move by this many. */
  columns: number;
}

/** One grid of choices (hours or minutes): a radiogroup with a roving tab stop. */
function ChoiceGrid({
  unit,
  labelId,
  options,
  selected,
  near,
  onChoose,
  tabStopRef,
  gridRef,
  columns,
}: ChoiceGridProps) {
  // Where the arrow keys moved the tab stop, for as long as the selection it moved from stands.
  const [moved, setMoved] = useState<{ index: number; from: number | null } | null>(null);
  const anchor = Math.max(0, options.indexOf(selected ?? near ?? options[0]!));
  const stop = moved && moved.from === selected ? moved.index : anchor;

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="radio"]'));
    const at = items.indexOf(e.target as HTMLButtonElement);
    if (at < 0) return;
    const last = items.length - 1;
    const target: Record<string, number> = {
      ArrowLeft: at - 1,
      ArrowRight: at + 1,
      ArrowUp: at - columns,
      ArrowDown: at + columns,
      Home: 0,
      End: last,
    };
    const next = target[e.key];
    if (next === undefined) return;
    e.preventDefault();
    if (next < 0 || next > last) return;
    setMoved({ index: next, from: selected });
    items[next]!.focus();
  };

  return (
    <div
      ref={gridRef}
      role="radiogroup"
      aria-labelledby={labelId}
      className="picker-time__grid"
      data-unit={unit}
      onKeyDown={onKeyDown}
    >
      {options.map((n, i) => {
        const checked = n === selected;
        return (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={i === stop ? 0 : -1}
            ref={i === stop ? tabStopRef : undefined}
            className={[
              'picker-time__cell',
              checked ? 'is-selected' : '',
              selected === null && n === near ? 'is-near' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => {
              setMoved(null);
              onChoose(n);
            }}
          >
            {pad2(n)}
          </button>
        );
      })}
    </div>
  );
}

export interface PickerTimeChooserProps {
  value: HourMinute;
  onChange: (next: HourMinute) => void;
  /** Group label, e.g. "From time". */
  label: string;
  /** The minute grid's step (5). */
  minuteStep?: number;
  /** Receives the hour that holds the tab stop (a time-only sheet focuses it on open). */
  hourRef?: Ref<HTMLButtonElement>;
  /** The whole group (a date + time sheet scrolls it into view after a day is picked). */
  ref?: Ref<HTMLDivElement>;
  /** Announce the chosen time politely; off where the sheet's own hint already announces it. */
  announce?: boolean;
  /** Told whether "Type a time" holds something unreadable (the sheet keeps Apply off). */
  onTypedInvalidChange?: (invalid: boolean) => void;
  /** Receives the "Type a time" box (the desktop time popup focuses it on open). */
  typedRef?: Ref<HTMLInputElement>;
  /** Enter in "Type a time" with a readable time (or nothing typed): the popup applies. */
  onEnter?: () => void;
  /**
   * A time on its own in the desktop popup (ThemedTimePicker): a wide, short chooser — the hours
   * in two rows of twelve (00–11, 12–23) and the minutes in one row — so the whole chooser fits
   * a laptop screen without scrolling. Beside a calendar, and on a phone, it stays six across.
   */
  wide?: boolean;
}

/** What was typed, and the time it stood for (the text is dropped once the time changes). */
interface TypedEntry {
  text: string;
  at: HourMinute;
}

export default function PickerTimeChooser({
  value,
  onChange,
  label,
  minuteStep = 5,
  hourRef,
  ref,
  announce = true,
  onTypedInvalidChange,
  typedRef,
  onEnter,
  wide = false,
}: PickerTimeChooserProps) {
  const columns = wide ? WIDE_COLUMNS : COLUMNS;
  // The desktop popup is keyboard-first: "Type a time" comes before the grids.
  const typedFirst = usePickerVariant() === 'popover';
  const labelId = useId();
  const hourLabelId = useId();
  const minuteLabelId = useId();
  const typedId = useId();
  const helpId = useId();
  const errorId = useId();
  const minutesRef = useRef<HTMLDivElement | null>(null);
  const minutes = useMemo(() => minuteGrid(minuteStep), [minuteStep]);
  const onGrid = minutes.includes(value.m);

  const [entry, setEntry] = useState<TypedEntry | null>(null);
  const [showError, setShowError] = useState(false);
  // A grid tap, a quick time or the other end of a range changed the time: the text is stale.
  const text = entry && sameTime(entry.at, value) ? entry.text : '';
  const typedInvalid = text.trim() !== '' && !parseTypedTime(text);
  const errorShown = typedInvalid && showError;

  const report = useLatest(onTypedInvalidChange);
  useEffect(() => {
    report.current?.(typedInvalid);
  }, [typedInvalid, report]);
  useEffect(() => () => report.current?.(false), [report]);

  const chooseHour = (h: number) => {
    setEntry(null);
    onChange({ h, m: value.m });
    // On to the minutes (their tab stop does not move with the hour).
    const grid = minutesRef.current;
    grid?.querySelector<HTMLButtonElement>('[role="radio"][tabindex="0"]')?.focus({ preventScroll: true });
    revealTime(grid);
  };

  const chooseMinute = (m: number) => {
    setEntry(null);
    onChange({ h: value.h, m });
  };

  const type = (next: string) => {
    setShowError(false);
    const t = parseTypedTime(next);
    setEntry({ text: next, at: t ?? value });
    if (t && !sameTime(t, value)) onChange(t);
  };

  // Leaving the field (or Enter) tidies a readable time to HH:MM and explains an unreadable one.
  const settle = () => {
    const t = parseTypedTime(text);
    if (t) setEntry({ text: formatHm(t), at: t });
    else if (text.trim()) setShowError(true);
  };

  const typedEntry = (
    <>
      <div className="picker-time__exact">
        <label htmlFor={typedId} className="picker-time__exact-label">
          Type a time
        </label>
        <input
          ref={typedRef}
          id={typedId}
          type="text"
          inputMode="numeric"
          enterKeyHint="done"
          autoComplete="off"
          spellCheck={false}
          className="picker-time__input"
          placeholder="e.g. 1435"
          value={text}
          aria-invalid={errorShown || undefined}
          aria-describedby={errorShown ? `${errorId} ${helpId}` : helpId}
          onChange={(e) => type(e.target.value)}
          onBlur={settle}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            settle();
            if (onEnter && !typedInvalid) onEnter();
          }}
        />
      </div>
      {errorShown && (
        <p id={errorId} className="picker-time__error" role="alert">
          That is not a time. Type it like 1435 or 14:35.
        </p>
      )}
      <p id={helpId} className="picker-time__help">
        For any minute, e.g. 1435, 14:35 or 2:35 pm.
      </p>
    </>
  );

  return (
    <div
      ref={ref}
      className={`picker-time${wide ? ' picker-time--wide' : ''}`}
      role="group"
      aria-labelledby={labelId}
    >
      <div className="picker-time__head">
        <p id={labelId} className="picker-time__label">
          {label}
        </p>
        <span
          className="picker-time__readout"
          aria-live={announce ? 'polite' : undefined}
          aria-atomic={announce ? true : undefined}
        >
          {formatHm(value)}
        </span>
      </div>

      {typedFirst && typedEntry}

      <p id={hourLabelId} className="picker-time__caption">
        Hour
      </p>
      <ChoiceGrid
        unit="hour"
        labelId={hourLabelId}
        options={HOURS}
        selected={value.h}
        near={null}
        onChoose={chooseHour}
        tabStopRef={hourRef}
        columns={columns}
      />

      <p id={minuteLabelId} className="picker-time__caption">
        Minute
      </p>
      <ChoiceGrid
        unit="minute"
        labelId={minuteLabelId}
        options={minutes}
        selected={onGrid ? value.m : null}
        near={onGrid ? null : nearestGridMinute(value.m, minuteStep)}
        onChoose={chooseMinute}
        gridRef={minutesRef}
        columns={columns}
      />

      {!typedFirst && typedEntry}
    </div>
  );
}
